/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useEffect } from 'react'

import { FiChevronsRight, FiMenu, FiX } from 'react-icons/fi'
import { TbLayoutSidebarLeftCollapse, TbPinned } from 'react-icons/tb'
import { useLocation } from 'react-router-dom'

import { Button } from '@/components/ui'
import Sidebar, { COLLAPSED_WIDTH, DEFAULT_WIDTH, isCollapsedWidth } from '@/components/ui/Sidebar/Sidebar'
import useLocalStorage from '@/hooks/useLocalStorage'
import DashboardNavigationSidebar from '@/pages/dashboard/components/DashboardNavigationSidebar'

import { NavLogo } from './NavLogo'
import { useNavMode } from './NavModeContext'

/** Before the sidebar was resizable it only collapsed; start from that choice (collapsed by default). */
const initialWidth = (): number => {
  try {
    return window.localStorage.getItem('dashboardNavigationSidebarCollapsed') === 'false'
      ? DEFAULT_WIDTH
      : COLLAPSED_WIDTH
  } catch {
    return COLLAPSED_WIDTH
  }
}

const iconButton = 'h-8 w-8 min-h-0 p-0 rounded-full border-none shadow-none text-base-content/60'

/**
 * The app's left nav. Pinned, it's a resizable panel beside the content; as a hamburger, it
 * shrinks to a small floating button at the top left that opens the same nav as an overlay drawer.
 */
const AppNav: React.FC = () => {
  // Kept in this browser only, not in the user's profile.
  const [sidebarWidth, setSidebarWidth] = useLocalStorage('dashboardNavigationSidebarWidth', initialWidth())
  const { setMode, canPin, isCollapsed, drawerOpen, setDrawerOpen } = useNavMode()
  const location = useLocation()

  // Picking anything in the drawer navigates; take the drawer away with it.
  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname, setDrawerOpen])

  useEffect(() => {
    if (!drawerOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [drawerOpen, setDrawerOpen])

  if (!isCollapsed) {
    const isIconOnly = isCollapsedWidth(sidebarWidth)
    return (
      <Sidebar
        width={sidebarWidth}
        onWidthChange={setSidebarWidth}
        header={
          <div
            className={`flex items-center gap-1 shrink-0 px-3 pt-3 pb-2 ${isIconOnly ? 'flex-col' : 'justify-between'}`}
          >
            <NavLogo compact={isIconOnly} />
            {isIconOnly && (
              <Button
                variation="ghost"
                size="sm"
                className={iconButton}
                ariaLabel="Expand menu"
                handleClick={() => setSidebarWidth(DEFAULT_WIDTH)}
              >
                <FiChevronsRight className="h-5 w-5" />
              </Button>
            )}
            <Button
              variation="ghost"
              size="sm"
              className={iconButton}
              ariaLabel="Collapse menu"
              handleClick={() => setMode('hamburger')}
            >
              <TbLayoutSidebarLeftCollapse className="h-5 w-5" />
            </Button>
          </div>
        }
      >
        <DashboardNavigationSidebar isIconOnly={isIconOnly} />
      </Sidebar>
    )
  }

  return (
    <>
      {/* Floats over the page's title bar, whose text starts clear of it. Layout isolates <main>,
          so no z-index inside a page can cover it. */}
      <button
        type="button"
        className="fixed top-[3px] left-[3px] z-40 flex items-center justify-center h-8 w-8 rounded-full cursor-pointer bg-base-100 text-base-content shadow-md border border-base-300 hover:bg-base-200"
        aria-label="Open menu"
        onClick={() => setDrawerOpen(true)}
      >
        <FiMenu className="h-4 w-4" />
      </button>
      {drawerOpen && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/30"
            data-testid="nav-drawer-backdrop"
            onClick={() => setDrawerOpen(false)}
          />
          <aside
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
            className="fixed inset-y-0 left-0 z-50 w-96 max-w-[85vw] bg-base-100 border-r border-base-300 shadow-xl flex flex-col"
          >
            <div className="flex items-center justify-between gap-1 shrink-0 px-3 pt-3 pb-2">
              <NavLogo />
              <div className="flex items-center">
                {canPin && (
                  <Button
                    variation="ghost"
                    size="sm"
                    className={iconButton}
                    ariaLabel="Pin menu"
                    handleClick={() => {
                      setDrawerOpen(false)
                      setMode('pinned')
                    }}
                  >
                    <TbPinned className="h-5 w-5" />
                  </Button>
                )}
                <Button
                  variation="ghost"
                  size="sm"
                  className={iconButton}
                  ariaLabel="Close menu"
                  handleClick={() => setDrawerOpen(false)}
                >
                  <FiX className="h-5 w-5" />
                </Button>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pb-4">
              <DashboardNavigationSidebar isIconOnly={false} />
            </div>
          </aside>
        </>
      )}
    </>
  )
}

export default AppNav
